import { useEffect } from 'react';

import { DEFAULT_SKILL_OPTIONS_LIMIT } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { useOnboardingQuestionData } from 'bundles/onboarding-common/contexts/OnboardingQuestionDataContext';
import useBaseSelectState from 'bundles/onboarding-common/hooks/useBaseSelectState';
import useHandleSelectedItemsChange from 'bundles/onboarding-common/hooks/useHandleSelectedItemsChange';
import type { SearchAndSelectOptionWithState, SelectOptionWithState, Skill } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

const getInitialSkillsState = (
  skills: Skill[],
  selectedSkills: Skill[] = []
): SearchAndSelectOptionWithState<Skill>[] => {
  const selectedIds = new Set(selectedSkills.map((skill) => skill.id));

  const selectedSkillsWithState = selectedSkills.map((skill) => ({
    ...skill,
    isSelected: true,
    isSearchResult: false,
    isVisible: true,
    isPartOfSearch: false,
  }));

  const unselectedSkillsWithState = skills
    .filter((skill) => !selectedIds.has(skill.id))
    .map((skill) => ({
      ...skill,
      isSelected: false,
      isSearchResult: false,
      isVisible: true,
      isPartOfSearch: false,
    }));

  return [...selectedSkillsWithState, ...unselectedSkillsWithState];
};

const useSkillSelectQuestionState = ({
  skills,
  skillsWantedData,
}: {
  skills: Skill[];
  skillsWantedData: Skill[] | null;
}) => {
  const { onChange, setValidationErrorMessage } = useOnboardingQuestionData();

  const defaultSingleSelectOption: SelectOptionWithState<Skill> = {
    id: 'i-dont-know',
    skillName: _t("I don't know"),
    isSelected: false,
  };

  const {
    itemsList,
    itemsPerRow: skillsPerRow,
    containerRef,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
    iDontKnowOption: iDontKnowSkill,
  } = useBaseSelectState({
    questionId: QUESTION_IDS.skillSelect,
    defaultSingleSelectOption,
    getDisplayName: (item) => item.skillName || '',
    widthData: {
      itemPadding: 24,
      iconWidth: 0,
      spacingBetweenIconAndText: 0,
    },
    initialState: getInitialSkillsState(skills, skillsWantedData || []),
  });

  const handleSelectedSkillsChange = (allSkills: SelectOptionWithState<Skill>[]) => {
    const selectedSkills = allSkills
      .filter(({ isSelected }) => isSelected)
      .map(({ id, skillName }) => ({ id, skillName: skillName || '' }));

    onChange({
      __typename: QUESTION_IDS.skillSelect,
      value: selectedSkills,
    });
  };

  useHandleSelectedItemsChange([...itemsList, ...(iDontKnowSkill ? [iDontKnowSkill] : [])], handleSelectedSkillsChange);

  useEffect(() => {
    if (itemsList.filter(({ isSelected }) => isSelected).length > DEFAULT_SKILL_OPTIONS_LIMIT) {
      setValidationErrorMessage(
        _t("You've reached the limit of #{skillsLimit} selected skills", { skillsLimit: DEFAULT_SKILL_OPTIONS_LIMIT })
      );
    } else {
      setValidationErrorMessage(undefined);
    }
  }, [itemsList, setValidationErrorMessage]);

  return {
    skillsPerRow,
    containerRef,
    handleClick,
    onViewMoreClick,
    onViewAllClick,
    isLoading,
    isExpanded,
  };
};

export default useSkillSelectQuestionState;
